import React, { useState } from 'react';
import {
  Sparkles,
  X,
  CheckCircle,
  AlertTriangle,
  Info,
  ShieldAlert,
  ExternalLink,
  ChevronRight,
  BookOpen,
  ArrowRight,
  HelpCircle,
  Calendar,
  Layers
} from 'lucide-react';
import { AnnouncementBanner, FeatureHighlightItem, BannerTone } from '../types.ts';
import { DynamicIcon } from './DynamicIcon.tsx';

interface FeatureShowcaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcement: AnnouncementBanner | null;
  allAnnouncements?: AnnouncementBanner[];
  onDismissAnnouncement?: (id: string, dontShowAgain: boolean) => void;
  onNavigateTab?: (tabId: string) => void;
}

const BADGE_STYLES: Record<string, string> = {
  NEW: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  IMPROVED: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  'PRO TIP': 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30',
  BETA: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  IMPORTANT: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30',
  'E-SIGN': 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/30',
  SECURITY: 'bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/30',
};

const TONE_GRADIENTS: Record<BannerTone, { bg: string; badge: string; iconBg: string; text: string }> = {
  feature: {
    bg: 'from-indigo-600 via-blue-600 to-indigo-800 text-white',
    badge: 'bg-indigo-400/20 text-indigo-100 border-indigo-300/30',
    iconBg: 'bg-white/20 text-white',
    text: 'text-indigo-600 dark:text-indigo-400'
  },
  info: {
    bg: 'from-blue-600 to-sky-700 text-white',
    badge: 'bg-blue-400/20 text-blue-100 border-blue-300/30',
    iconBg: 'bg-white/20 text-white',
    text: 'text-blue-600 dark:text-blue-400'
  },
  success: {
    bg: 'from-emerald-600 to-teal-700 text-white',
    badge: 'bg-emerald-400/20 text-emerald-100 border-emerald-300/30',
    iconBg: 'bg-white/20 text-white',
    text: 'text-emerald-600 dark:text-emerald-400'
  },
  warning: {
    bg: 'from-amber-600 to-orange-700 text-white',
    badge: 'bg-amber-400/20 text-amber-100 border-amber-300/30',
    iconBg: 'bg-white/20 text-white',
    text: 'text-amber-600 dark:text-amber-400'
  },
  alert: {
    bg: 'from-rose-600 to-red-700 text-white',
    badge: 'bg-rose-400/20 text-rose-100 border-rose-300/30',
    iconBg: 'bg-white/20 text-white',
    text: 'text-rose-600 dark:text-rose-400'
  },
};

export default function FeatureShowcaseModal({
  isOpen,
  onClose,
  announcement,
  allAnnouncements = [],
  onDismissAnnouncement,
  onNavigateTab
}: FeatureShowcaseModalProps) {
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  if (!isOpen) return null;

  // Determine active announcement to showcase
  const active =
    (selectedAnnouncementId && allAnnouncements.find(a => a.id === selectedAnnouncementId)) ||
    announcement ||
    allAnnouncements[0] ||
    null;

  if (!active) return null;

  const tone = active.tone || 'feature';
  const toneStyle = TONE_GRADIENTS[tone] || TONE_GRADIENTS.feature;

  const handleClose = () => {
    if (active.dismissible && onDismissAnnouncement && dontShowAgain) {
      onDismissAnnouncement(active.id, true);
    }
    onClose();
  };

  const handleExplicitDismiss = () => {
    if (active.dismissible !== false && onDismissAnnouncement) {
      onDismissAnnouncement(active.id, true);
    }
    onClose();
  };

  const handleCardAction = (item: FeatureHighlightItem) => {
    if (!item.actionUrl) return;
    if (item.actionUrl.startsWith('http://') || item.actionUrl.startsWith('https://')) {
      window.open(item.actionUrl, '_blank', 'noopener,noreferrer');
    } else if (onNavigateTab) {
      onNavigateTab(item.actionUrl);
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Backdrop with modern blur */}
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-950 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh] z-10 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header Hero Banner */}
        <div className={`p-6 sm:p-8 bg-gradient-to-br ${toneStyle.bg} relative shrink-0`}>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg bg-black/20 text-white/80 hover:text-white hover:bg-black/30 transition cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider font-mono border ${toneStyle.badge}`}>
              FEATURE SPOTLIGHT
            </span>
            {active.createdAt && (
              <span className="text-[11px] font-medium text-white/80">
                {new Date(active.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>

          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white flex items-center gap-2">
            {active.title}
          </h2>

          <p className="mt-2 text-xs sm:text-sm text-white/90 font-medium leading-relaxed max-w-xl">
            {active.message}
          </p>

          {/* Multiple Announcements Tab Bar if more than 1 available */}
          {allAnnouncements.length > 1 && (
            <div className="mt-4 flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar border-t border-white/20 pt-3">
              <span className="text-[10px] uppercase font-bold tracking-wider text-white/70 mr-1 shrink-0">
                Updates:
              </span>
              {allAnnouncements.map((ann) => (
                <button
                  key={ann.id}
                  onClick={() => setSelectedAnnouncementId(ann.id)}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold whitespace-nowrap transition cursor-pointer shrink-0 ${
                    active.id === ann.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {ann.title.length > 25 ? `${ann.title.substring(0, 25)}...` : ann.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Feature Highlight Cards Grid */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-3">
          {(!active.featureItems || active.featureItems.length === 0) ? (
            <div className="py-8 text-center text-slate-500 dark:text-slate-400 text-xs">
              No specific highlight cards attached to this announcement.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {active.featureItems.map((item) => {
                const badgeStyle = (item.badge && BADGE_STYLES[item.badge]) || BADGE_STYLES.NEW;
                return (
                  <div
                    key={item.id || item.title}
                    className="p-4 rounded-xl border border-slate-200 dark:border-slate-800/80 bg-slate-50/70 dark:bg-slate-900/50 hover:border-slate-300 dark:hover:border-slate-700 transition flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-950/70 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-xs">
                          <DynamicIcon name={item.iconName} className="w-4 h-4" />
                        </div>
                        {item.badge && (
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded border font-mono tracking-wider uppercase ${badgeStyle}`}>
                            {item.badge}
                          </span>
                        )}
                      </div>

                      <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
                        {item.title}
                      </h3>

                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-normal">
                        {item.description}
                      </p>
                    </div>

                    {item.actionLabel && (
                      <div className="mt-3 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                        <button
                          onClick={() => handleCardAction(item)}
                          className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 transition cursor-pointer"
                        >
                          <span>{item.actionLabel}</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:px-6 py-3.5 bg-slate-50 dark:bg-slate-900/60 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
          {/* Dismissal Checkbox */}
          {active.dismissible !== false ? (
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-700 dark:bg-slate-900"
              />
              <span>Don't show this announcement automatically again</span>
            </label>
          ) : (
            <div className="text-[11px] text-slate-400 font-mono">System Announcement</div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => {
                if (onNavigateTab) {
                  onNavigateTab('help-center');
                }
                handleClose();
              }}
              className="px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer flex items-center gap-1.5"
            >
              <BookOpen className="w-3.5 h-3.5 text-slate-400" />
              <span>Help Center</span>
            </button>

            {active.dismissible !== false && (
              <button
                onClick={handleExplicitDismiss}
                className="px-3.5 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-200/80 dark:bg-slate-800 hover:bg-slate-300/80 dark:hover:bg-slate-700 rounded-lg transition cursor-pointer"
              >
                Dismiss
              </button>
            )}

            <button
              onClick={() => {
                if (active.ctaUrl) {
                  if (active.ctaUrl.startsWith('http://') || active.ctaUrl.startsWith('https://')) {
                    window.open(active.ctaUrl, '_blank', 'noopener,noreferrer');
                  } else if (onNavigateTab) {
                    onNavigateTab(active.ctaUrl);
                  }
                }
                handleClose();
              }}
              className="px-4 py-1.5 text-xs font-extrabold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] rounded-lg transition cursor-pointer shadow-sm flex items-center gap-1.5"
            >
              <span>{active.ctaLabel || 'Got It / Explore'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
